import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMemo } from 'react';

/**
 * Custom hook for managing competition tab routing and URL parameters
 *
 * Provides:
 * - Current tab index based on URL path
 * - Navigation functions for tabs
 * - Query parameter management
 * - Next.js compatible URL structure
 */
export const useCompetitionRouting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Map URL paths to tab indices
  const TAB_ROUTES = {
    schedule: 0,
    sessions: 1,
    athletes: 2,
    judges: 3,
    statistics: 4,
    venue: 5,
    results: 6,
  };

  const TAB_PATHS = ['schedule', 'sessions', 'athletes', 'judges', 'statistics', 'venue', 'results'];

  // Determine current tab from URL path
  const currentTab = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    return TAB_ROUTES[lastPart] ?? 0;
  }, [location.pathname]);

  // Get current query params
  const searchParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  // Navigate to a specific tab
  // Options: { preserveParams: boolean, queryParams: object }
  const navigateToTab = (tabIndex, options = {}) => {
    // Support legacy boolean argument for backwards compatibility
    const opts = typeof options === 'boolean'
      ? { preserveParams: options }
      : options;
    const { preserveParams = false, queryParams = {} } = opts;

    const tabPath = TAB_PATHS[tabIndex];
    if (!tabPath) return;

    const newPath = `/competition/${id}/${tabPath}`;

    // Build query string from existing params (if preserving) and new params
    const newSearchParams = new URLSearchParams(preserveParams ? searchParams : {});
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        newSearchParams.set(key, String(value));
      }
    });

    const search = newSearchParams.toString();
    navigate(`${newPath}${search ? `?${search}` : ''}`);
  };

  // Update query parameters without changing tab
  const updateQueryParams = (params, replace = false) => {
    const newSearchParams = new URLSearchParams(replace ? {} : searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, String(value));
      }
    });

    const newSearch = newSearchParams.toString();
    const pathParts = location.pathname.split('/');
    const basePath = pathParts.slice(0, -1).join('/');
    const tabPath = pathParts[pathParts.length - 1];

    navigate(`${basePath}/${tabPath}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  // Get a specific query parameter
  const getQueryParam = (key, defaultValue = null) => {
    return searchParams.get(key) || defaultValue;
  };

  // Get all query parameters as object
  const getAllQueryParams = () => {
    const params = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  };

  return {
    currentTab,
    navigateToTab,
    updateQueryParams,
    getQueryParam,
    getAllQueryParams,
    competitionId: id,
  };
};
